import axios from 'axios';
import { sanitizeTimeForApi } from './utils/time';

// Base URL for the backend API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://fnolbackend.alphatorana.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
});

const unwrapEvidencePayload = (payload) => {
  if (
    payload &&
    typeof payload === 'object' &&
    Object.prototype.hasOwnProperty.call(payload, 'success') &&
    Object.prototype.hasOwnProperty.call(payload, 'data')
  ) {
    return payload.data;
  }

  return payload;
};

// FNOL API functions
export const fnolAPI = {
  // Submit a new FNOL
  submitFNOL: async (fnolData) => {
    const formattedData = {
      loss_name: fnolData.loss_name,
      claimant: fnolData.claimant || null,
      date_of_loss: fnolData.date_of_loss || null,
      time_of_loss: sanitizeTimeForApi(fnolData.time_of_loss),
      cause_of_loss: fnolData.cause_of_loss || null,
      loss_description: fnolData.loss_description || null,
      estimated_loss_currency: fnolData.estimated_loss_currency || null,
      estimated_loss_amount: fnolData.estimated_loss_amount || null,
      loss_location: fnolData.loss_location || null,
      gps_coordinates: fnolData.gps_coordinates || null,
      report_date: fnolData.report_date || null,
      reported_by: fnolData.reported_by || null,
      global_loss_details: fnolData.global_loss_details || null,
      policy_number: fnolData.policy_number || null,
      multiple_policy_number: fnolData.multiple_policy_number || null
    };

    try {
      const response = await api.post('/api/fnol/submit', formattedData);
      // Response will contain: fnol_id, status, message, created_at
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Get FNOLs (fetches all pages by default)
  getFNOLs: async (status = null, options = {}) => {
    const {
      fetchAll = true,
      limit = 100,
      page = 1,
    } = options;

    const baseParams = {};
    if (status !== null) baseParams.status = status;

    const fetchPage = async (pageNumber, pageLimit) => {
      const params = {
        ...baseParams,
        _ts: Date.now(),
      };

      if (pageNumber !== null && pageNumber !== undefined) params.page = pageNumber;
      if (pageLimit !== null && pageLimit !== undefined) params.limit = pageLimit;

      const response = await api.get('/api/fnol/', {
        params,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      return response.data || {};
    };

    if (!fetchAll) {
      return fetchPage(page, limit);
    }

    const normalizedLimit = Number.isFinite(Number(limit)) && Number(limit) > 0
      ? Number(limit)
      : 100;

    let currentPage = 1;
    let hasNext = true;
    let safetyCounter = 0;
    const maxPages = 200;

    const allItems = [];
    let lastPayload = null;
    let totalCountFromServer = null;

    while (hasNext && safetyCounter < maxPages) {
      safetyCounter += 1;

      const payload = await fetchPage(currentPage, normalizedLimit);
      lastPayload = payload;

      const pageItems = Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload.results)
          ? payload.results
          : [];

      allItems.push(...pageItems);

      const pagination = payload.pagination || {};
      const totalPages = Number(pagination.total_pages);

      if (Number.isFinite(Number(pagination.total_count))) {
        totalCountFromServer = Number(pagination.total_count);
      }

      if (typeof pagination.has_next === 'boolean') {
        hasNext = pagination.has_next;
      } else if (Number.isFinite(totalPages)) {
        hasNext = currentPage < totalPages;
      } else {
        hasNext = pageItems.length === normalizedLimit;
      }

      currentPage += 1;
    }

    if (!lastPayload) {
      return {
        success: true,
        data: [],
        count: 0,
        pagination: {
          page: 1,
          limit: normalizedLimit,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_prev: false,
        },
      };
    }

    const totalCount = totalCountFromServer ?? allItems.length;

    return {
      ...lastPayload,
      data: allItems,
      count: totalCount,
      pagination: {
        ...(lastPayload.pagination || {}),
        page: 1,
        limit: normalizedLimit,
        total_count: totalCount,
        total_pages: Math.max(1, Math.ceil(totalCount / normalizedLimit)),
        has_next: false,
        has_prev: false,
      },
    };
  },

  // Get a specific FNOL by ID (details)
  getFNOL: async (fnolId) => {
    const response = await api.get(`/api/fnol/${fnolId}`);
    return response.data;
  },

  // Update FNOL status
  updateFNOLStatus: async (fnolId, status) => {
    const response = await api.put(`/api/fnol/${fnolId}/status`, null, {
      params: { status },
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });
    return response.data;
  },

  // Update existing FNOL
  updateFNOL: async (fnolId, fnolData) => {
    const response = await api.put(`/api/fnol/${fnolId}`, {
      ...fnolData,
      time_of_loss: sanitizeTimeForApi(fnolData.time_of_loss),
    });
    return response.data;
  },

  // Get policy details by policy number
  getPolicyDetails: async (policyNumber) => {
    const response = await api.get(`/api/fnol/policy/${policyNumber}`);
    return response.data;
  },

  // Request more data for FNOL
  requestMoreData: async (requestData) => {
    const response = await api.post('/api/fnol/request-more-data', requestData);
    return response.data;
  },

  // Get validity check results for FNOL
  getValidityCheck: async (fnolId) => {
    try {
      const response = await api.get(`/api/validity/${fnolId}`);
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return { success: false, notFound: true, message: error.response.data.detail || 'Validity record not found' };
      }
      throw error;
    }
  },

  // Create validity check for FNOL
  createValidityCheck: async (fnolId) => {
    const response = await api.post('/api/validity_check', { fnol_id: fnolId });
    return response.data;
  },

  // Delete validity check for FNOL
  deleteValidityCheck: async (fnolId) => {
    const response = await api.delete(`/api/validity/${fnolId}`);
    return response.data;
  },
};

// Claims API functions
export const claimsAPI = {
  // Get all claims
  getAllClaims: async () => {
    const response = await api.get('/api/claims/');
    return response.data;
  },

  // Get claim by FNOL ID
  getClaim: async (fnolId) => {
    try {
      const response = await api.get(`/api/claims/${fnolId}`);
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return { success: false, notFound: true, message: error.response.data.detail || 'No claim found for FNOL' };
      }
      throw error;
    }
  },

  // Assign claims team
  assignClaims: async (assignmentData) => {
    const response = await api.post('/api/claims/assign', assignmentData);
    return response.data;
  },

  // Get claims assignment
  getClaimsAssignment: async (fnolId) => {
    const response = await api.get(`/api/claims/${fnolId}/assignment`);
    return response.data;
  },

  // Update claims assignment
  updateClaimsAssignment: async (fnolId, updateData) => {
    const response = await api.put(`/api/claims/${fnolId}/assignment`, null, {
      params: updateData
    });
    return response.data;
  },

  // Escalate claim
  escalateClaim: async (fnolId, reason) => {
    const response = await api.post(`/api/claims/${fnolId}/escalate`, null, {
      params: { reason }
    });
    return response.data;
  },

  // Get available teams
  getTeams: async () => {
    const response = await api.get('/api/claims/teams');
    return response.data;
  },

  // Get available handlers
  getHandlers: async () => {
    const response = await api.get('/api/claims/handlers');
    return response.data;
  },

  // Get available adjusters
  getAdjusters: async () => {
    const response = await api.get('/api/claims/adjusters');
    return response.data;
  },
};

// Document API functions
export const documentsAPI = {
  // Upload document
  uploadDocument: async (formData) => {
    const response = await api.post('/api/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get documents for FNOL
  getDocuments: async (fnolId) => {
    const response = await api.get(`/api/documents/${fnolId}/attachments`);
    return response.data;
  },
};

// Fraud analysis API functions
export const fraudAPI = {
  // Get fraud analysis results for FNOL
  getFraudAnalysis: async (fnolId) => {
    try {
      const response = await api.get(`/api/fraud_analysis/${fnolId}`);
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return { success: false, notFound: true, message: error.response.data.detail || 'Fraud analysis record not found' };
      }
      throw error;
    }
  },

  // Analyze for fraud
  analyzeFraud: async (analysisData) => {
    const response = await api.post('/api/fraud/analyze', analysisData);
    return response.data;
  },

  // Create fraud analysis for FNOL
  createFraudAnalysis: async (fnolId) => {
    const response = await api.post('/api/fraud_analysis', { fnol_id: fnolId });
    return response.data;
  },

  // Request more data for fraud analysis
  requestMoreData: async (requestData) => {
    const response = await api.post('/api/fraud/request-more-data', requestData);
    return response.data;
  },
};

export const evidenceAPI = {
  // Get original evidence for FNOL
  getOriginalEvidence: async (fnolId) => {
    const response = await api.get(`/api/evidence/original/${fnolId}`);
    return unwrapEvidencePayload(response.data);
  },

  // Get added evidence for FNOL
  getAddedEvidence: async (fnolId) => {
    try {
      const response = await api.get(`/api/evidence/added/${fnolId}`);
      return { success: true, data: unwrapEvidencePayload(response.data) };
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return { success: false, notFound: true, message: 'No added evidence found' };
      }
      throw error;
    }
  },

  // Create added evidence for FNOL
  createAddedEvidence: async (fnolId, data) => {
    const response = await api.post(`/api/evidence/added/${fnolId}`, data);
    return response.data;
  },

  // Update added evidence for FNOL
  updateAddedEvidence: async (fnolId, data) => {
    const response = await api.put(`/api/evidence/added/${fnolId}`, data);
    return response.data;
  },
};

// Dashboard API functions
export const dashboardAPI = {
  // Get dashboard statistics
  getStatistics: async (filters = {}) => {
    const params = new URLSearchParams();
    params.append('_ts', Date.now().toString());
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.location && filters.location !== 'All') params.append('location', filters.location);
    if (filters.global_event && filters.global_event !== 'All') params.append('global_event', filters.global_event);
    if (filters.estimated_loss_range && filters.estimated_loss_range !== 'All') params.append('estimated_loss_range', filters.estimated_loss_range);

    const response = await api.get(`/api/dashboard/statistics${params.toString() ? '?' + params.toString() : ''}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });
    return response.data;
  },

  // Get an authoritative FNOL total count from list metadata as a fallback
  getFNOLCount: async () => {
    const response = await api.get('/api/fnol/', {
      params: { _ts: Date.now() },
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });

    const payload = response.data || {};
    const candidateValues = [
      payload.total_count,
      payload.count,
      payload.pagination?.total_count,
      payload.meta?.total_count,
      payload.meta?.total,
      payload.data?.total_count,
      payload.data?.count,
    ];

    const firstNumeric = candidateValues.find((value) => Number.isFinite(Number(value)));
    if (firstNumeric !== undefined) {
      return Number(firstNumeric);
    }

    if (Array.isArray(payload.data)) return payload.data.length;
    if (Array.isArray(payload.results)) return payload.results.length;
    return null;
  },

  // Get FNOL distribution for pie chart
  getFNOLDistribution: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.location && filters.location !== 'All') params.append('location', filters.location);
    if (filters.global_event && filters.global_event !== 'All') params.append('global_event', filters.global_event);
    if (filters.estimated_loss_range && filters.estimated_loss_range !== 'All') params.append('estimated_loss_range', filters.estimated_loss_range);

    const response = await api.get(`/api/dashboard/fnol-distribution${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  // Get pending activity data
  getPendingActivity: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.location && filters.location !== 'All') params.append('location', filters.location);
    if (filters.global_event && filters.global_event !== 'All') params.append('global_event', filters.global_event);
    if (filters.estimated_loss_range && filters.estimated_loss_range !== 'All') params.append('estimated_loss_range', filters.estimated_loss_range);

    const response = await api.get(`/api/dashboard/pending-activity${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  // Get lifecycle timeline data
  getLifecycleTimeline: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.period) params.append('period', options.period);
    if (options.date_from) params.append('date_from', options.date_from);
    if (options.date_to) params.append('date_to', options.date_to);
    if (options.granularity) params.append('granularity', options.granularity);
    if (options.location && options.location !== 'All') params.append('location', options.location);
    if (options.global_event && options.global_event !== 'All') params.append('global_event', options.global_event);

    const response = await api.get(`/api/dashboard/lifecycle-timeline${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  // Get aging buckets data
  getAgingBuckets: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.location && filters.location !== 'All') params.append('location', filters.location);
    if (filters.global_event && filters.global_event !== 'All') params.append('global_event', filters.global_event);
    if (filters.estimated_loss_range && filters.estimated_loss_range !== 'All') params.append('estimated_loss_range', filters.estimated_loss_range);

    const response = await api.get(`/api/dashboard/aging-buckets${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  // Get triaging by team data
  getTriagingByTeam: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.location && filters.location !== 'All') params.append('location', filters.location);
    if (filters.global_event && filters.global_event !== 'All') params.append('global_event', filters.global_event);
    if (filters.estimated_loss_range && filters.estimated_loss_range !== 'All') params.append('estimated_loss_range', filters.estimated_loss_range);

    const response = await api.get(`/api/dashboard/triaging-by-team${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  // Get assignment analysis data
  getAssignmentAnalysis: async (userId, options = {}) => {
    const params = new URLSearchParams();
    params.append('user_id', userId);
    if (options.date_from) params.append('date_from', options.date_from);
    if (options.date_to) params.append('date_to', options.date_to);
    if (options.include_monthly !== undefined) params.append('include_monthly', options.include_monthly.toString());

    const response = await api.get(`/api/dashboard/assignment-analysis?${params.toString()}`);
    return response.data;
  },
};

export default api;
